import { useCallback } from 'react';

import { useJoinLessonNowMutation } from 'core/graphql';

type JoinLessonResultType = {
  success: boolean;
  redirectUrl?: string | null;
};

type JoinLessonType = [
  (groupLessonId: number) => Promise<JoinLessonResultType>,
  { loading: boolean },
];

/**
 * Spec 0013: records the join click on the backend and returns the redirect
 * URL. The join URL is never available through queries — only from this
 * mutation. Backend failures (no link configured / no access) arrive as a
 * top-level GraphQL error, so the returned promise REJECTS in that case —
 * callers must catch.
 */
export const useJoinLesson = (): JoinLessonType => {
  const [joinLessonMutation, { loading }] = useJoinLessonNowMutation();

  const joinLesson = useCallback(
    async (groupLessonId: number): Promise<JoinLessonResultType> => {
      const { data } = await joinLessonMutation({ variables: { groupLessonId } });
      const payload = data?.lmsJoinLesson;

      return {
        success: Boolean(payload?.success && payload?.redirectUrl),
        redirectUrl: payload?.redirectUrl,
      };
    },
    [joinLessonMutation],
  );

  return [joinLesson, { loading }];
};
